import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import useEscapeKey from '../hooks/useEscapeKey'
import useFocusTrap from '../hooks/useFocusTrap'
import './NoProjectsPrompt.css'

const NoProjectsPrompt = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const dialogRef = useFocusTrap(isOpen)
  useEscapeKey(onClose, isOpen)

  if (!isOpen) return null

  const handleCreateProject = () => {
    onClose()
    navigate('/projects')
  }

  // Use createPortal to render directly to the document body
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{
        background: 'rgba(10, 14, 22, 0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'noprojects-backdrop-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="noprojects-title"
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{
          animation: 'noprojects-modal-in 320ms cubic-bezier(0.22, 1, 0.36, 1) both',
          transformOrigin: 'center center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 bg-gradient-to-r from-blue-500 to-cyan-400">
          <h2 id="noprojects-title" className="text-2xl font-bold text-white text-center">No Projects Available</h2>
        </div>

        <div className="p-8 text-center">
          <div className="mb-6 mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-gray-600 mb-6">
            You need to create a project before you can start editing. Would you like to create one now?
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleCreateProject}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-lg hover:from-blue-600 hover:to-cyan-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 font-medium"
            >
              Create New Project
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

NoProjectsPrompt.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default NoProjectsPrompt
